import { useReducer, useEffect, useMemo, useRef } from "react";
import { useAppDispatch } from "@/store";
import {
  useLazyGetDatasetQuestionsQuery,
  useLazyGetDatasetsPaginatedQuery,
  useLazyGetDatasetQuestionsListQuery,
  useGetDatasetByIdQuery,
} from "@/store/api/dataset-api";
import {
  buildDefaultDateGuardrails,
  type TimeSeriesUnit,
} from "@/models/Dataset";
import { showErrorSnackbar } from "@/store/slices/snackbar-slice";
import { t } from "@/utils/i18n";
import { useDebounce } from "@/hooks/use-debounce";
import type {
  Dataset,
  DatasetQuestionsSearchResponse,
  QuestionDetail,
} from "@/models/Dataset";
import type { ChartType } from "@/utils/chart-utils";
import type { LineFocusMode } from "@/store/slices/line-chart-config-slice";
import type { Client } from "@/models/Dashboards";
import { groupDatasets } from "@/utils/group-datasets";
import { questionSessionMemory } from "@/utils/question-session-memory";

interface UseChartModalProps {
  hasActiveFilters?: boolean;
  existingDatasetIds?: string[];
  primaryClient?: Client;
  additionalClients?: Client[];
  dashboardId?: string;
}

// State type
interface ChartModalState {
  datasetId: string;
  datasetVersionId: number | null;
  // Dataset list pagination
  datasetSearchTerm: string;
  datasetPage: number;
  allDatasets: Dataset[];
  datasetsHasMore: boolean;
  datasetsHasError: boolean;
  // Question list
  searchTerm: string;
  selectedQuestionId: string;
  autoUpdate: boolean;
  currentPage: number;
  allQuestions: DatasetQuestionsSearchResponse[];
  hasMore: boolean;
  hasError: boolean;
  isAutoSelected: boolean;
  // Line chart focus mode state
  selectedQuestionDetails: QuestionDetail | null;
  focusMode: LineFocusMode | null;
  selectedVariableId: string | null;
  selectedValueId: string | null;
  selectedVariableLabel: string | null;
  selectedValueValue: string | null;
  // Line chart time unit
  timeUnit: "QUARTER" | "SEMESTER" | "MONTH" | "WEEK";
}

// Action types
type ChartModalAction =
  | { type: "SET_DATASET_ID"; payload: string }
  | { type: "SET_DATASET_VERSION_ID"; payload: number | null }
  | { type: "SET_DATASET_SEARCH_TERM"; payload: string }
  | { type: "SET_DATASET_PAGE"; payload: number }
  | { type: "SET_ALL_DATASETS"; payload: Dataset[] }
  | { type: "APPEND_DATASETS"; payload: Dataset[] }
  | { type: "SET_DATASETS_HAS_MORE"; payload: boolean }
  | { type: "SET_DATASETS_HAS_ERROR"; payload: boolean }
  | { type: "RESET_DATASET_PAGINATION" }
  | { type: "SET_SEARCH_TERM"; payload: string }
  | { type: "SET_SELECTED_QUESTION_ID"; payload: string }
  | { type: "SET_AUTO_UPDATE"; payload: boolean }
  | { type: "SET_CURRENT_PAGE"; payload: number }
  | { type: "SET_ALL_QUESTIONS"; payload: DatasetQuestionsSearchResponse[] }
  | { type: "APPEND_QUESTIONS"; payload: DatasetQuestionsSearchResponse[] }
  | { type: "SET_HAS_MORE"; payload: boolean }
  | { type: "SET_HAS_ERROR"; payload: boolean }
  | { type: "RESET_PAGINATION" }
  | { type: "SET_QUESTION_DETAILS"; payload: QuestionDetail | null }
  | { type: "SET_FOCUS_MODE"; payload: LineFocusMode | null }
  | { type: "SET_SELECTED_VARIABLE_ID"; payload: string | null }
  | { type: "SET_SELECTED_VALUE_ID"; payload: string | null }
  | { type: "SET_SELECTED_VARIABLE_LABEL"; payload: string | null }
  | { type: "SET_SELECTED_VALUE_VALUE"; payload: string | null }
  | { type: "SET_IS_AUTO_SELECTED"; payload: boolean }
  | {
      type: "SET_TIME_UNIT";
      payload: "QUARTER" | "SEMESTER" | "MONTH" | "WEEK";
    };

// Initial state
const initialState: ChartModalState = {
  datasetId: "",
  datasetVersionId: null,
  datasetSearchTerm: "",
  datasetPage: 1,
  allDatasets: [],
  datasetsHasMore: true,
  datasetsHasError: false,
  searchTerm: "",
  selectedQuestionId: "",
  autoUpdate: false,
  currentPage: 1,
  allQuestions: [],
  hasMore: true,
  hasError: false,
  isAutoSelected: false,
  selectedQuestionDetails: null,
  focusMode: null,
  selectedVariableId: null,
  selectedValueId: null,
  selectedVariableLabel: null,
  selectedValueValue: null,
  timeUnit: "QUARTER",
};

// Reducer function
const chartModalReducer = (
  state: ChartModalState,
  action: ChartModalAction,
): ChartModalState => {
  switch (action.type) {
    case "SET_DATASET_ID":
      return { ...state, datasetId: action.payload };
    case "SET_DATASET_VERSION_ID":
      return { ...state, datasetVersionId: action.payload };
    case "SET_DATASET_SEARCH_TERM":
      return { ...state, datasetSearchTerm: action.payload };
    case "SET_DATASET_PAGE":
      return { ...state, datasetPage: action.payload };
    case "SET_ALL_DATASETS":
      return { ...state, allDatasets: action.payload };
    case "APPEND_DATASETS": {
      const existingDatasetIds = new Set(state.allDatasets.map((d) => d.id));
      const newDatasets = action.payload.filter(
        (d) => !existingDatasetIds.has(d.id),
      );
      return { ...state, allDatasets: [...state.allDatasets, ...newDatasets] };
    }
    case "SET_DATASETS_HAS_MORE":
      return { ...state, datasetsHasMore: action.payload };
    case "SET_DATASETS_HAS_ERROR":
      return { ...state, datasetsHasError: action.payload };
    case "RESET_DATASET_PAGINATION":
      return {
        ...state,
        datasetPage: 1,
        allDatasets: [],
        datasetsHasMore: true,
        datasetsHasError: false,
      };
    case "SET_SEARCH_TERM":
      return { ...state, searchTerm: action.payload };
    case "SET_SELECTED_QUESTION_ID":
      return { ...state, selectedQuestionId: action.payload };
    case "SET_AUTO_UPDATE":
      return { ...state, autoUpdate: action.payload };
    case "SET_CURRENT_PAGE":
      return { ...state, currentPage: action.payload };
    case "SET_ALL_QUESTIONS":
      return { ...state, allQuestions: action.payload };
    case "APPEND_QUESTIONS":
      // Filter out duplicates based on datasetElementId
      const existingIds = new Set(
        state.allQuestions.map((q) => q.datasetElementId),
      );
      const newQuestions = action.payload.filter(
        (q) => !existingIds.has(q.datasetElementId),
      );
      return {
        ...state,
        allQuestions: [...state.allQuestions, ...newQuestions],
      };
    case "SET_HAS_MORE":
      return { ...state, hasMore: action.payload };
    case "SET_HAS_ERROR":
      return { ...state, hasError: action.payload };
    case "RESET_PAGINATION":
      return {
        ...state,
        currentPage: 1,
        allQuestions: [],
        hasMore: true,
        hasError: false,
      };
    case "SET_QUESTION_DETAILS":
      return { ...state, selectedQuestionDetails: action.payload };
    case "SET_FOCUS_MODE":
      return {
        ...state,
        focusMode: action.payload,
        // Reset selections when mode changes
        selectedVariableId: null,
        selectedValueId: null,
      };
    case "SET_SELECTED_VARIABLE_ID":
      return { ...state, selectedVariableId: action.payload };
    case "SET_SELECTED_VALUE_ID":
      return { ...state, selectedValueId: action.payload };
    case "SET_SELECTED_VARIABLE_LABEL":
      return { ...state, selectedVariableLabel: action.payload };
    case "SET_SELECTED_VALUE_VALUE":
      return { ...state, selectedValueValue: action.payload };
    case "SET_TIME_UNIT":
      return { ...state, timeUnit: action.payload };
    case "SET_IS_AUTO_SELECTED":
      return { ...state, isAutoSelected: action.payload };
    default:
      return state;
  }
};

export const useChartModal = (
  chartType?: ChartType,
  props?: UseChartModalProps,
) => {
  const {
    hasActiveFilters = false,
    existingDatasetIds = [],
    primaryClient,
    additionalClients = [],
    dashboardId = "",
  } = props || {};
  const dispatch = useAppDispatch();
  const [state, dispatchState] = useReducer(chartModalReducer, initialState);
  const observerTarget = useRef<HTMLDivElement>(null);
  const observerTargetDatasets = useRef<HTMLDivElement>(null);

  const [
    triggerDatasets,
    { error: datasetsError, isFetching: isFetchingDatasets },
  ] = useLazyGetDatasetsPaginatedQuery();
  const [trigger, { data: questionsData, error: questionsError, isFetching }] =
    useLazyGetDatasetQuestionsQuery();
  const [triggerPinned, { data: pinnedQuestionsData }] =
    useLazyGetDatasetQuestionsQuery();
  const [fetchQuestionDetails, { isFetching: isFetchingQuestionDetails }] =
    useLazyGetDatasetQuestionsListQuery();

  const { data: datasetDetails } = useGetDatasetByIdQuery(state.datasetId, {
    skip: !state.datasetId || chartType !== "line",
  });

  // When a dataset is selected for a line chart, apply the guardrail default time unit
  useEffect(() => {
    if (chartType !== "line" || !state.datasetId) return;
    const tsGuardrails =
      datasetDetails?.dateGuardrails?.timeSeries ??
      buildDefaultDateGuardrails().timeSeries;
    const defaultUnit = tsGuardrails.defaultUnit.toUpperCase() as
      | "QUARTER"
      | "SEMESTER"
      | "MONTH"
      | "WEEK";
    dispatchState({ type: "SET_TIME_UNIT", payload: defaultUnit });
  }, [chartType, state.datasetId, datasetDetails]);

  // Debounce search terms
  const debouncedSearchTerm = useDebounce(state.searchTerm, 500);
  const debouncedDatasetSearch = useDebounce(state.datasetSearchTerm, 400);

  // Determine question types based on chart type - memoized to prevent infinite loops
  const questionTypes = useMemo(() => {
    if (chartType === "table") {
      return ["FLOAT", "NUMERIC"];
    } else if (chartType === "line") {
      // Line charts support all question types per PRD:
      // - Float/Numeric: Each variable = line (mean values)
      // - Multiple: Each variable = line (percentage)
      // - Single: Each value = line (percentage)
      return ["FLOAT", "NUMERIC", "SINGLE", "MULTI"];
    } else {
      // Bar, Donut, Pie charts support Single and Multi
      return ["SINGLE", "MULTI"];
    }
  }, [chartType]);

  // Fetch recent/pinned questions by ID when dataset is selected (no search active)
  useEffect(() => {
    if (!state.datasetId || !dashboardId) return;
    const recentIds = questionSessionMemory
      .getRecent(dashboardId, state.datasetId, "general")
      .map((e) => e.questionId);
    if (recentIds.length === 0) return;
    triggerPinned({
      id: state.datasetId,
      dataset_element_ids: recentIds,
      page_size: recentIds.length,
      question_type: questionTypes,
      multidimensional: chartType === "line" || chartType === "bar",
    });
  }, [state.datasetId, dashboardId, triggerPinned, questionTypes, chartType]);

  // Load first page when dataset or search term changes
  useEffect(() => {
    if (state.datasetId) {
      dispatchState({ type: "RESET_PAGINATION" });
      trigger({
        id: state.datasetId,
        page: 1,
        page_size: 30,
        question_type: questionTypes,
        multidimensional:
          chartType === "line" || chartType === "bar" ? true : false,
        search_query: debouncedSearchTerm || undefined,
      })
        .unwrap()
        .catch(() => {
          dispatchState({ type: "SET_HAS_ERROR", payload: true });
        });
    }
  }, [state.datasetId, debouncedSearchTerm, trigger, questionTypes]);

  // Accumulate questions as pages load
  useEffect(() => {
    if (questionsData?.questions) {
      if (state.currentPage === 1) {
        dispatchState({
          type: "SET_ALL_QUESTIONS",
          payload: questionsData.questions,
        });
      } else {
        dispatchState({
          type: "APPEND_QUESTIONS",
          payload: questionsData.questions,
        });
      }
      dispatchState({
        type: "SET_HAS_MORE",
        payload: questionsData.pagination.hasNextPage,
      });
    }
  }, [questionsData, state.currentPage]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          state.hasMore &&
          !isFetching &&
          !state.hasError &&
          state.datasetId
        ) {
          const nextPage = state.currentPage + 1;
          dispatchState({ type: "SET_CURRENT_PAGE", payload: nextPage });
          trigger({
            id: state.datasetId,
            page: nextPage,
            page_size: 10,
            question_type: questionTypes,
            multidimensional:
              chartType === "line" || chartType === "bar" ? true : false,
            search_query: debouncedSearchTerm || undefined,
          })
            .unwrap()
            .catch(() => {
              dispatchState({ type: "SET_HAS_ERROR", payload: true });
              dispatchState({ type: "SET_HAS_MORE", payload: false });
            });
        }
      },
      { threshold: 0.1 },
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [
    state.hasMore,
    isFetching,
    state.hasError,
    state.datasetId,
    state.currentPage,
    debouncedSearchTerm,
    trigger,
    questionTypes,
  ]);

  // ── Dataset list: load first page when search term changes ──────────────────
  useEffect(() => {
    dispatchState({ type: "RESET_DATASET_PAGINATION" });
    triggerDatasets({
      state: "active",
      name: debouncedDatasetSearch || undefined,
      page: 1,
      page_size: 30,
    })
      .unwrap()
      .then((res) => {
        dispatchState({ type: "SET_ALL_DATASETS", payload: res.datasets });
        dispatchState({
          type: "SET_DATASETS_HAS_MORE",
          payload: res.pagination.hasNextPage,
        });
      })
      .catch(() =>
        dispatchState({ type: "SET_DATASETS_HAS_ERROR", payload: true }),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedDatasetSearch, hasActiveFilters]);

  // ── Dataset list: infinite scroll ───────────────────────────────────────────
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          state.datasetsHasMore &&
          !isFetchingDatasets &&
          !state.datasetsHasError
        ) {
          const nextPage = state.datasetPage + 1;
          dispatchState({ type: "SET_DATASET_PAGE", payload: nextPage });
          triggerDatasets({
            state: "active",
            name: debouncedDatasetSearch || undefined,
            page: nextPage,
            page_size: 10,
          })
            .unwrap()
            .then((res) => {
              dispatchState({ type: "APPEND_DATASETS", payload: res.datasets });
              dispatchState({
                type: "SET_DATASETS_HAS_MORE",
                payload: res.pagination.hasNextPage,
              });
            })
            .catch(() => {
              dispatchState({ type: "SET_DATASETS_HAS_ERROR", payload: true });
              dispatchState({ type: "SET_DATASETS_HAS_MORE", payload: false });
            });
        }
      },
      { threshold: 0.1 },
    );
    const el = observerTargetDatasets.current;
    if (el) observer.observe(el);
    return () => {
      if (el) observer.unobserve(el);
    };
  }, [
    state.datasetsHasMore,
    isFetchingDatasets,
    state.datasetsHasError,
    state.datasetPage,
    debouncedDatasetSearch,
    triggerDatasets,
  ]);

  // ── Error handling ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (datasetsError) {
      dispatch(showErrorSnackbar({ message: t("common.error") }));
    }
  }, [datasetsError, dispatch]);

  useEffect(() => {
    if (questionsError) {
      dispatch(showErrorSnackbar({ message: t("common.error") }));
      dispatchState({ type: "SET_HAS_ERROR", payload: true });
      dispatchState({ type: "SET_HAS_MORE", payload: false });
    }
  }, [questionsError, dispatch]);

  // ── Auto-select when exactly one dataset is in use ───────────────────────────
  useEffect(() => {
    if (
      existingDatasetIds.length === 1 &&
      state.allDatasets.length > 0 &&
      !state.datasetId
    ) {
      const id = existingDatasetIds[0];
      const dataset = state.allDatasets.find((d) => d.id === id);
      if (dataset) {
        dispatchState({ type: "SET_DATASET_ID", payload: id });
        dispatchState({
          type: "SET_DATASET_VERSION_ID",
          payload: dataset.datasetVersionId ?? null,
        });
        dispatchState({ type: "SET_IS_AUTO_SELECTED", payload: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.allDatasets, existingDatasetIds]);

  // ── Computed values ──────────────────────────────────────────────────────────
  const datasetGroups = useMemo(() => {
    // When filters are active, restrict to existingDatasetIds regardless of search
    const baseDatasets =
      hasActiveFilters && existingDatasetIds.length > 0
        ? state.allDatasets.filter((d) => existingDatasetIds.includes(d.id))
        : state.allDatasets;
    return groupDatasets(
      baseDatasets,
      existingDatasetIds,
      primaryClient,
      additionalClients,
    );
  }, [
    state.allDatasets,
    hasActiveFilters,
    existingDatasetIds,
    primaryClient,
    additionalClients,
  ]);

  // When no search is active, prepend pinned (recently used) questions fetched by ID.
  // pinnedQuestionsData comes from the targeted dataset_element_ids fetch — these are
  // ordered by recentIds order (most recent first) and deduplicated from the main list.
  const filteredQuestions = useMemo(() => {
    if (state.searchTerm || !dashboardId || !state.datasetId) {
      return state.allQuestions;
    }

    const recentIds = questionSessionMemory
      .getRecent(dashboardId, state.datasetId, "general")
      .map((e) => e.questionId);

    if (recentIds.length === 0) return state.allQuestions;

    // Use API-fetched pinned questions when available; fall back to scanning allQuestions
    const pinnedSource = pinnedQuestionsData?.questions ?? state.allQuestions;
    const recentSet = new Set(recentIds);

    const pinned = recentIds
      .map((id) => pinnedSource.find((q) => q.datasetElementId === id))
      .filter(Boolean) as typeof state.allQuestions;

    const rest = state.allQuestions.filter(
      (q) => !recentSet.has(q.datasetElementId),
    );

    return [...pinned, ...rest];
  }, [
    state.allQuestions,
    state.searchTerm,
    dashboardId,
    state.datasetId,
    pinnedQuestionsData,
  ]);

  // Handlers
  const handleDatasetChange = (value: string) => {
    dispatchState({ type: "SET_DATASET_ID", payload: value });
    dispatchState({ type: "SET_SELECTED_QUESTION_ID", payload: "" });
    dispatchState({ type: "SET_SEARCH_TERM", payload: "" });

    // Find the selected dataset and set its datasetVersionId
    const selectedDataset = state.allDatasets.find((d) => d.id === value);
    if (selectedDataset?.datasetVersionId) {
      dispatchState({
        type: "SET_DATASET_VERSION_ID",
        payload: selectedDataset.datasetVersionId,
      });
    } else {
      dispatchState({ type: "SET_DATASET_VERSION_ID", payload: null });
    }
  };

  const handleDatasetSearchChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    dispatchState({
      type: "SET_DATASET_SEARCH_TERM",
      payload: event.target.value,
    });
  };

  const handleQuestionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatchState({ type: "SET_SEARCH_TERM", payload: event.target.value });
  };

  const handleQuestionSelect = async (
    question: DatasetQuestionsSearchResponse,
  ) => {
    dispatchState({
      type: "SET_SELECTED_QUESTION_ID",
      payload: question.datasetElementId,
    });

    // For line and bar charts with Single questions, fetch full question details to check variable count
    if (
      (chartType === "line" || chartType === "bar") &&
      question.type?.toUpperCase() === "SINGLE" &&
      state.datasetId
    ) {
      try {
        const result = await fetchQuestionDetails({
          id: state.datasetId,
          page: 1,
          page_size: 10,
          search_query: question.label, //TODO: remove whe BE ready
          dataset_element_id: question.datasetElementId,
          expanded: true,
        }).unwrap();

        // Find the matching question in the response
        const questionDetail = result.questions.find(
          (q) => q.datasetElementId === question.datasetElementId,
        );

        if (questionDetail) {
          dispatchState({
            type: "SET_QUESTION_DETAILS",
            payload: questionDetail,
          });

          // If question has multiple variables, we need focus mode selection
          const totalVariables =
            (questionDetail.rowVariables?.length || 0) +
            (questionDetail.columnVariables?.length || 0);

          if (totalVariables > 1) {
            // Set default focus mode to "variable" (like crosstab does)
            dispatchState({ type: "SET_FOCUS_MODE", payload: "variable" });
          } else {
            // Single variable, no focus mode needed
            dispatchState({ type: "SET_QUESTION_DETAILS", payload: null });
          }
        }
      } catch (error) {
        console.error("Failed to fetch question details:", error);
      }
    } else {
      // Not a line/bar chart or not a Single question, clear focus state
      dispatchState({ type: "SET_QUESTION_DETAILS", payload: null });
      dispatchState({ type: "SET_FOCUS_MODE", payload: null });
    }
  };

  const handleFocusModeChange = (mode: LineFocusMode) => {
    dispatchState({ type: "SET_FOCUS_MODE", payload: mode });
  };

  const handleVariableSelect = (variableId: string, variableLabel?: string) => {
    dispatchState({ type: "SET_SELECTED_VARIABLE_ID", payload: variableId });
    if (variableLabel) {
      dispatchState({
        type: "SET_SELECTED_VARIABLE_LABEL",
        payload: variableLabel,
      });
    }
  };

  const handleValueSelect = (valueId: string, valueValue?: string) => {
    dispatchState({ type: "SET_SELECTED_VALUE_ID", payload: valueId });
    if (valueValue) {
      dispatchState({ type: "SET_SELECTED_VALUE_VALUE", payload: valueValue });
    }
  };

  const setAutoUpdate = (value: boolean) => {
    dispatchState({ type: "SET_AUTO_UPDATE", payload: value });
  };

  const handleTimeUnitChange = (
    value: "QUARTER" | "SEMESTER" | "MONTH" | "WEEK",
  ) => {
    dispatchState({ type: "SET_TIME_UNIT", payload: value });
  };

  return {
    // State
    datasetId: state.datasetId,
    datasetVersionId: state.datasetVersionId,
    searchTerm: state.searchTerm,
    selectedQuestionId: state.selectedQuestionId,
    selectedQuestionType: (
      state.allQuestions.find(
        (q) => q.datasetElementId === state.selectedQuestionId,
      ) ??
      pinnedQuestionsData?.questions.find(
        (q) => q.datasetElementId === state.selectedQuestionId,
      )
    )?.type,
    autoUpdate: state.autoUpdate,
    hasMore: state.hasMore,
    isFetching,
    observerTarget,
    // Line chart focus state
    selectedQuestionDetails: state.selectedQuestionDetails,
    focusMode: state.focusMode,
    selectedVariableId: state.selectedVariableId,
    selectedValueId: state.selectedValueId,
    selectedVariableLabel: state.selectedVariableLabel,
    selectedValueValue: state.selectedValueValue,
    timeUnit: state.timeUnit,
    enabledTimeUnits: (() => {
      if (chartType !== "line") return null;
      const tsGuardrails =
        datasetDetails?.dateGuardrails?.timeSeries ??
        buildDefaultDateGuardrails().timeSeries;
      return (["QUARTER", "SEMESTER", "MONTH", "WEEK"] as const).filter(
        (u) => tsGuardrails.units[u.toLowerCase() as TimeSeriesUnit].enabled,
      );
    })(),

    isAutoSelected: state.isAutoSelected,

    // Computed
    datasetGroups,
    filteredQuestions,
    recentQuestionIds:
      dashboardId && state.datasetId
        ? questionSessionMemory
            .getRecent(dashboardId, state.datasetId, "general")
            .map((e) => e.questionId)
        : [],
    hasActiveFilters,
    isDatasetRestricted: hasActiveFilters && existingDatasetIds.length > 0,
    // Check if focus mode selection is needed
    needsFocusSelection:
      (chartType === "line" || chartType === "bar") &&
      state.selectedQuestionDetails !== null,
    isFetchingQuestionDetails,

    // Dataset search
    datasetSearchTerm: state.datasetSearchTerm,
    datasetsHasMore: state.datasetsHasMore,
    handleDatasetSearchChange,
    isFetchingDatasets,
    observerTargetDatasets,

    // Handlers
    handleDatasetChange,
    handleQuestionChange,
    handleQuestionSelect,
    setAutoUpdate,
    handleFocusModeChange,
    handleVariableSelect,
    handleValueSelect,
    handleTimeUnitChange,
  };
};
